import InvitationStatus from "@/modules/reviews/InvitationStatus";
import { walkInPaymentMethodLabel } from "@/services/payment/walkInPaymentMethod";
import useIsSmall from "@/hooks/useIsSmall";
import { PurchasePaymentStatus, SnappPayStatus } from "@/models/club";
import useGetInvoiceDetail from "@/services/customer/hooks/useGetInvoiceDetail";
import { useInquirePurchasePayment } from "@/services/payment/utils";
import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import moment from "moment-jalaali";
import { DepositGatewayBadge } from "./DepositGatewayBadge";

type Props = {
  purchaseId?: number;
  opened: boolean;
  onClose: () => void;
};

const money = (value?: number | null) =>
  typeof value === "number" ? `${value.toLocaleString("fa-IR")} ریال` : "ـ";

const statusLabel = (status?: PurchasePaymentStatus) => {
  if (status === PurchasePaymentStatus.Value1) return { label: "پرداخت‌شده", color: "green" };
  if (status === PurchasePaymentStatus.Value3) return { label: "ناموفق", color: "red" };
  return { label: "در انتظار پرداخت", color: "yellow" };
};

const attemptLabel = (status?: SnappPayStatus) => {
  const labels = ["در انتظار", "تأییدشده", "تسویه‌شده", "ناموفق", "لغوشده", "مغایرت — در انتظار استعلام"];
  const colors = ["yellow", "blue", "green", "red", "gray", "orange"];
  return { label: labels[status ?? 0] || "نامشخص", color: colors[status ?? 0] || "gray" };
};

function InvoiceDetailsModal({ purchaseId, opened, onClose }: Props) {
  const isSmall = useIsSmall();
  const { data, isLoading } = useGetInvoiceDetail(opened ? purchaseId : undefined);
  const status = statusLabel(data?.paymentStatus);
  const inquiry = useInquirePurchasePayment(purchaseId);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={4}>جزئیات فاکتور #{purchaseId}</Title>}
      size="xl"
      fullScreen={isSmall}
      centered
    >
      <LoadingOverlay visible={isLoading} />
      {data && (
        <Stack gap="md">
          {purchaseId && <InvitationStatus key={purchaseId} purchaseId={purchaseId} hideWhenNotRequested />}
          <Card withBorder p="md">
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Text fw={800}>{data.customer?.firstName} {data.customer?.lastName}</Text>
                <Text size="xs" c="dimmed">{data.customer?.phoneNumber || "شماره تماس ثبت نشده"}</Text>
                <Text size="xs" c="dimmed">{data.merchantTitle || ""}</Text>
              </Stack>
              <Badge color={status.color} variant="light" size="lg">{status.label}</Badge>
            </Group>
            <Divider my="sm" />
            <Group justify="space-between">
              <Text size="xs" c="dimmed">کد سفارش: {data.transactionId || "پرداخت مستقیم"}</Text>
              <Text size="xs" c="dimmed">
                {data.creationTime ? moment(data.creationTime).format("jYYYY/jMM/jDD - HH:mm") : "ـ"}
              </Text>
            </Group>
          </Card>

          <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }}>
            <Card withBorder p="sm"><Text size="xs" c="dimmed">مبلغ کل</Text><Text fw={800}>{money(data.totalAmount)}</Text></Card>
            {(data.deposit || 0) > 0 && (
              <Card
                withBorder
                p="sm"
                bg="blue.0"
                style={{ borderColor: "var(--mantine-color-blue-3)" }}
              >
                <Text size="xs" c="blue.7">بیعانه پرداخت‌شده برای رزرو</Text>
                <Text fw={800} c="blue.9">{money(data.deposit)}</Text>
              </Card>
            )}
            {(data.discountAmount || 0) > 0 && (
              <Card withBorder p="sm" bg="green.0" style={{ borderColor: "var(--mantine-color-green-3)" }}>
                <Text size="xs" c="green.8">تخفیف{data.discountCode ? ` (${data.discountCode})` : ""}</Text>
                <Text fw={800} c="green.9">−{money(data.discountAmount)}</Text>
              </Card>
            )}
            {(data.appointmentDiscountAmount || 0) > 0 && (
              <Card withBorder p="sm" bg="green.0" style={{ borderColor: "var(--mantine-color-green-3)" }}>
                <Text size="xs" c="green.8">تخفیف رزرو آنلاین</Text>
                <Text fw={800} c="green.9">−{money(data.appointmentDiscountAmount)}</Text>
              </Card>
            )}
            <Card withBorder p="sm"><Text size="xs" c="dimmed">دریافت حضوری</Text><Text fw={800}>{money(data.directAmount)}</Text></Card>
            <Card withBorder p="sm"><Text size="xs" c="dimmed">سهم اسنپ‌پی</Text><Text fw={800} c="violet">{money(data.creditAmount)}</Text></Card>
            <Card withBorder p="sm"><Text size="xs" c="dimmed">کمیسیون سالن</Text><Text fw={800}>{money(data.commissionAmount)}</Text></Card>
            <Card withBorder p="sm"><Text size="xs" c="dimmed">سهم آرایشگر</Text><Text fw={800}>{money(data.barberAmount)}</Text></Card>
          </SimpleGrid>

          {(data.depositTotalsByGateway?.length || 0) > 0 && (
            <Stack gap="xs">
              <Title order={5}>پیش‌پرداخت به تفکیک درگاه</Title>
              <Group gap="xs">
                {data.depositTotalsByGateway?.map((total) => (
                  <Card
                    key={total.bankGateway ?? "unknown"}
                    withBorder
                    p="sm"
                  >
                    <Stack gap={5}>
                      <DepositGatewayBadge
                        gateway={total.bankGateway}
                        amount={total.amount}
                      />
                      <Text size="xs" c="dimmed">
                        {total.bookingCount || 0} رزرو
                      </Text>
                    </Stack>
                  </Card>
                ))}
              </Group>
            </Stack>
          )}

          {(data.deposits?.length || 0) > 0 && (
            <Stack gap="xs">
              <Title order={5}>جزئیات پیش‌پرداخت رزروها</Title>
              <ScrollArea>
                <Table striped withTableBorder miw={900}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>شناسه رزرو</Table.Th>
                      <Table.Th>درگاه</Table.Th>
                      <Table.Th>پیش‌پرداخت</Table.Th>
                      <Table.Th>مبلغ فاکتور</Table.Th>
                      <Table.Th>مانده حضوری</Table.Th>
                      <Table.Th>تاریخ پرداخت</Table.Th>
                      <Table.Th>شماره مرجع</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.deposits?.map((deposit, index) => (
                      <Table.Tr key={`${deposit.appointmentQueueId ?? "deposit"}-${index}`}>
                        <Table.Td>#{deposit.appointmentQueueId || "ـ"}</Table.Td>
                        <Table.Td>
                          <DepositGatewayBadge
                            gateway={deposit.bankGateway}
                            amount={deposit.amount}
                            showAmount={false}
                          />
                        </Table.Td>
                        <Table.Td>{money(deposit.amount)}</Table.Td>
                        <Table.Td>{money(deposit.invoicedAmount)}</Table.Td>
                        <Table.Td>{money(deposit.remainingAmount)}</Table.Td>
                        <Table.Td>
                          {deposit.paymentDate
                            ? moment(deposit.paymentDate).format("jYYYY/jMM/jDD - HH:mm")
                            : "ـ"}
                        </Table.Td>
                        <Table.Td>{deposit.rrn || "ـ"}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Stack>
          )}

          <Stack gap="xs">
            <Title order={5}>خدمات فاکتور</Title>
            <ScrollArea>
              <Table striped withTableBorder miw={850}>
                <Table.Thead><Table.Tr>
                  <Table.Th>خدمت</Table.Th><Table.Th>ارائه‌دهنده</Table.Th><Table.Th>نوع</Table.Th><Table.Th>نحوه پرداخت حضوری</Table.Th>
                  <Table.Th>مبلغ</Table.Th><Table.Th>کمیسیون</Table.Th><Table.Th>سهم آرایشگر</Table.Th><Table.Th>تسویه</Table.Th>
                </Table.Tr></Table.Thead>
                <Table.Tbody>
                  {data.items?.map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td>{item.serviceTitle || "ـ"}</Table.Td>
                      <Table.Td>{item.merchantUserFullName || "ـ"}</Table.Td>
                      <Table.Td>{item.purchaseType === "Appointment" ? "نوبت" : "حضوری"}</Table.Td>
                      <Table.Td>{item.purchaseType === "Appointment" ? "—" : walkInPaymentMethodLabel(item.walkInPaymentMethod)}</Table.Td>
                      <Table.Td>{money(item.grossAmount)}</Table.Td>
                      <Table.Td>{money(item.commissionAmount)} ({item.commissionPercentage || 0}٪)</Table.Td>
                      <Table.Td>{money(item.barberAmount)}</Table.Td>
                      <Table.Td><Badge color={item.isSettled ? "green" : "yellow"} variant="light">{item.isSettled ? "تسویه‌شده" : "تسویه‌نشده"}</Badge></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>

          {(data.creditAttempts?.length || 0) > 0 && (
            <Stack gap="xs">
              <Title order={5}>تاریخچه پرداخت اسنپ‌پی</Title>
              {data.creditAttempts?.map((attempt) => {
                const attemptStatus = attemptLabel(attempt.status);
                return (
                  <Card key={attempt.transactionId} withBorder p="sm">
                    <Group justify="space-between">
                      <Stack gap={1}>
                        <Text size="sm" fw={700}>کد سفارش {attempt.transactionId}</Text>
                        <Text size="xs" c="dimmed">{attempt.creationTime ? moment(attempt.creationTime).format("jYYYY/jMM/jDD - HH:mm") : "ـ"}</Text>
                      </Stack>
                      <Badge color={attemptStatus.color} variant="light">{attemptStatus.label}</Badge>
                    </Group>
                    <Group mt="xs" gap="lg">
                      <Text size="xs">اعتباری: {money(attempt.amount)}</Text>
                      <Text size="xs">مستقیم: {money(attempt.externalSourceAmount)}</Text>
                      <Text size="xs">کل: {money(attempt.totalAmount)}</Text>
                    </Group>
                    {attempt.status === SnappPayStatus.Value5 && attempt.transactionId && (
                      <Alert mt="sm" color="orange" variant="light" icon={<IconAlertTriangle size={18} />} title="نتیجه پرداخت نامشخص است">
                        <Stack gap="xs">
                          <Text size="sm">
                            اسنپ‌پی نتیجه این پرداخت را اعلام نکرده و ممکن است مبلغ از مشتری کسر شده باشد. تا روشن شدن وضعیت، لینک پرداخت جدید ارسال نکنید.
                          </Text>
                          <Group>
                            <Button
                              size="xs"
                              color="orange"
                              leftSection={<IconRefresh size={15} />}
                              loading={inquiry.isLoading && inquiry.variables === attempt.transactionId}
                              disabled={inquiry.isLoading}
                              onClick={() => inquiry.mutate(attempt.transactionId!)}
                            >
                              استعلام وضعیت پرداخت
                            </Button>
                            {inquiry.isLoading && <Text size="xs" c="dimmed">ممکن است چند دقیقه طول بکشد…</Text>}
                          </Group>
                        </Stack>
                      </Alert>
                    )}
                  </Card>
                );
              })}
            </Stack>
          )}

          {data.description && <Text size="sm" c="dimmed">توضیحات: {data.description}</Text>}
        </Stack>
      )}
    </Modal>
  );
}

export default InvoiceDetailsModal;
